import BaseApi from './BaseApi.js';

/**
 * CounterApi — global (non-org-scoped) counter/history endpoints.
 * Endpoint base: /counter (no /org/{org} prefix).
 */
class CounterApi extends BaseApi {
	/**
	 * GET /api/v1/counter/history/{eventId}?organizationId={organizationId}
	 * Returns per-event Start / End / Total counter values for the resource(s)
	 * on the given event. Works for both aircraft and simulator.
	 *
	 * @param {string|number} eventId
	 * @param {string|number} organizationId
	 */
	static async getEventHistory(eventId, organizationId) {
		return await this.get(
			`/counter/history/${eventId}?organizationId=${encodeURIComponent(organizationId)}`
		);
	}

	/**
	 * POST /api/v1/counter/{counterId} — update an aircraft counter
	 * (Rem18 Task 4 — replaces legacy `/org/{org}/hangars/saveCounter/{id}/`).
	 *
	 * Body shape — `CounterRequestDto` (`symphony-holdshort/src/DTO/API/Request/Counter/CounterRequestDto.php`):
	 * ```
	 * {
	 *   value: string,            // required; digits + . or ,
	 *   comments: string,         // required (Assert\NotBlank)
	 *   eventId: number,          // optional — surfaces in history
	 *   organizationId: number,   // required (validateActiveOrganizationAccess)
	 *   organizationUnique: string, // optional fallback
	 *   aircraftId: number,       // optional
	 *   rate: string,             // optional
	 *   enableRate: bool,         // optional
	 *   oldValue: string,         // optional — history math
	 *   timezone: string          // default 'UTC'
	 * }
	 * ```
	 *
	 * Note: this route is GLOBAL (NOT `/org/{org}/...`). `organizationId` in the
	 * body drives the org check via `validateActiveOrganizationAccess`.
	 *
	 * @param {string|number} counterId  Counter row id (per-aircraft-counter pair).
	 * @param {Object} payload  See `CounterRequestDto.php`.
	 */
	static async editAircraftCounter(counterId, payload) {
		return await this.post(`/counter/${counterId}`, payload);
	}

	/**
	 * POST /api/v1/counter/simulator/{simulatorId} — update a simulator counter
	 * (Rem18 Task 4 — replaces legacy `/org/{org}/hangars/saveSimulatorCounter/{id}/`).
	 *
	 * @param {string|number} simulatorId  Simulator resource id.
	 * @param {Object} payload  See `CounterRequestDto.php` (same shape as aircraft).
	 */
	static async editSimulatorCounter(simulatorId, payload) {
		return await this.post(`/counter/simulator/${simulatorId}`, payload);
	}
}

export default CounterApi;
